"use strict";

const ToDoListController = function () {
  let dataView;
  let dataBySubject = null;
  let isFetching = false;
  let filterSummary = null;
  let listenersLoaded = false;

  var init = async function () {
    loadTreeListeners();

    if (
      !DataViewFunctions.getUserEnabledTabs(dataView, "list").includes("todo") ||
      Tree.get("activeTab") !== "todo"
    ) {
      return;
    }

    const alreadyLoaded = loadTodosIfNecessary();
    if (alreadyLoaded) {
      await rerenderToDoList();
    }
    getFilterSummary();
    ToDoList.loadDomListeners([{ target: ".open-fact-sheet", callback: openFactSheet }]);
  };

  var loadTreeListeners = function () {
    if (listenersLoaded) {
      return;
    } else {
      listenersLoaded = true;
    }

    Tree.watch({
      activeTab: ["activeTab"],
      dataView: ["dataView"],
      navToggleSelection: ["navToggleSelection"],
    }).on("update", onSmartFiltersUpdate);

    Tree.select("smartToDoFilters").on("update", onSmartFiltersUpdate);
  };

  var onSmartFiltersUpdate = function () {
    if (Tree.get("activeTab") === "todo") {
      loadTodos();
      filterSummary?.enable();
    } else {
      filterSummary?.disable();
    }
  };

  var sortPreparedData = function (todos) {
    for (const subject in todos) {
      const sort = ToDoList.getSorting(subject);
      const sortersDict = ToDoConfig.getToDoSubjectConfig([subject]).getSortersDictCallback();
      SortUtilities.sortRows(sort.key, sort.reversed, todos[subject], sortersDict);
    }

    return todos;
  };

  var prepareData = async function (todosBySubject) {
    const clonedData = {};
    for (const subject in todosBySubject) {
      clonedData[subject] = Misc.shallowCloneArrayObjects(todosBySubject[subject]);
    }

    if ("bmp" === dataView) {
      return await prepareBmpTodosData(clonedData);
    } else if ("fcs" === dataView) {
      return await prepareFcsTodosData(clonedData);
    } else {
      for (const subject in clonedData) {
        clonedData[subject] = _applyPrepareDataCallback(subject, clonedData[subject]);
      }
      return clonedData;
    }
  };

  var addAlternativeSubjects = function (todosBySubject) {
    _applyToAllSubjects(todosBySubject, (todos) => {
      todos.forEach((todo) => {
        todo.alternativeSubjects = "alternativeSubjects" in todo ? todo.alternativeSubjects : [];
      });
    });
  };

  var rerenderToDoList = async function () {
    const preparedData = await prepareData(dataBySubject);
    ToDoList.render(sortPreparedData(preparedData));
  };

  var _groupTodosByEnabledSubjects = function (todos) {
    const enabledSubjects = DataViewFunctions.getUserEnabledToDoSubjects(dataView);
    const filters = DataViewFunctions.getCurrentDataViewProperty("smartToDoFilters", dataView)
      ? Filters.getOfflineFilters()
      : null;
    const result = {};

    for (const subject of enabledSubjects) {
      result[subject] = [];
    }

    for (const todo of todos) {
      for (const todoSubject of todo.toDoSubjects) {
        if (
          enabledSubjects.includes(todoSubject) &&
          (!filters || ResourceFilters.passSmartToDoFilters(todo, filters, [todoSubject]))
        ) {
          result[todoSubject].push(todo);
        }
      }
    }

    return result;
  };

  var _prepareAllData = function (data) {
    data = Misc.shallowCloneArrayObjects(data);
    data = TableDataFunctions.prepareData(data, dataView, true);
    dataBySubject = _groupTodosByEnabledSubjects(data);
  };

  var _prepareCommonBmpFcsData = function (todosBySubject) {
    todosBySubject = filterOutAnnualInputsSettingsTodos(todosBySubject);
    addAlternativeSubjects(todosBySubject);
    _applyToAllSubjects(todosBySubject, BmpFcsToDoController.addBmpFcsRequiredFieldStrings);
    addPropertiesByTodoAssetType(todosBySubject);
    addScoreColorClasses(todosBySubject);
    roundScores(todosBySubject);

    return todosBySubject;
  };

  var prepareBmpTodosData = function (todosBySubject) {
    todosBySubject = _prepareCommonBmpFcsData(todosBySubject);

    const maintenanceNeededTodos = todosBySubject["maintenanceNeeded"];

    const assessmentDueTodos = todosBySubject["assessmentDue"];
    addAssessmentDueDateColorClass(assessmentDueTodos);
    addAssessmentType(assessmentDueTodos);

    const inventoryIncompleteTodos = todosBySubject["inventoryIncomplete"];
    inventoryIncompleteTodos.forEach((todo) => {
      todo.numMissingRequiredFields = todo.requiredFields.length;
    });

    addBmpIconClassesToTodos(todosBySubject);

    return {
      maintenanceNeeded: maintenanceNeededTodos,
      assessmentDue: assessmentDueTodos,
      inventoryIncomplete: inventoryIncompleteTodos,
    };
  };

  var prepareFcsTodosData = function (todosBySubject) {
    todosBySubject = _prepareCommonBmpFcsData(todosBySubject);

    const maintenanceNeededTodos = todosBySubject["maintenanceNeeded"];

    const assessmentDueTodos = todosBySubject["assessmentDue"];
    addAssessmentDueDateColorClass(assessmentDueTodos);
    addAssessmentType(assessmentDueTodos);

    const inventoryIncompleteTodos = todosBySubject["inventoryIncomplete"];
    inventoryIncompleteTodos.forEach((todo) => {
      todo.numMissingRequiredFields = todo.requiredFields.length;
    });

    addFcsIconClassesToTodos(todosBySubject);

    return {
      maintenanceNeeded: maintenanceNeededTodos,
      assessmentDue: assessmentDueTodos,
      inventoryIncomplete: inventoryIncompleteTodos,
    };
  };

  var loadTodosIfNecessary = function (filters = null) {
    if (isFetching || !dataBySubject || Tree.get("dataView") !== dataView) {
      loadTodos(filters);
      return false;
    }

    return true;
  };

  // Does not fetch new data for G2 layers
  var loadTodos = async function (filters = null, loadingScreen = true) {
    if (isFetching) {
      return;
    }

    isFetching = true;
    const newDataView = Tree.get("dataView");
    if (dataView !== newDataView) {
      dataBySubject = null;
      dataView = newDataView;
    }

    setData(await Actions.getTodos(dataView, filters, loadingScreen));
    isFetching = false;
  };

  var filterOutAnnualInputsSettingsTodos = function (todosBySubject) {
    _applyToAllSubjects(todosBySubject, (todos, subject) => {
      todosBySubject[subject] = todos.filter(
        (todo) => todo.subject !== "annual reporting settings",
      );
    });
    return todosBySubject;
  };

  var openIncompleteInventoryModalForBmp = function (idBmp) {
    BmpFcsInventoryModalController.invokeBmpInventoryModal(idBmp);
  };

  var addPropertiesByTodoAssetType = function (todosBySubject) {
    var scoreProp = ToDoFunctions.getTodoPropertyNameByAssetType("score");
    var daysRemaningProp = ToDoFunctions.getTodoPropertyNameByAssetType("days_remaining");
    var obsDateProp = ToDoFunctions.getTodoPropertyNameByAssetType("obs_date");
    var dueDateProp = ToDoFunctions.getTodoPropertyNameByAssetType("due_date");

    _applyToAllSubjects(todosBySubject, (todos) => {
      todos.forEach((todo) => {
        todo.score = todo[scoreProp];
        todo.days_remaining = todo[daysRemaningProp];
        todo.obs_date = todo[obsDateProp];
        todo.noObsDate = todo.obs_date === null;
        todo.due_date = todo[dueDateProp];
      });
    });
  };

  var addBmpIconClassesToTodos = function (todosBySubject) {
    _applyToAllSubjects(todosBySubject, (todos) => {
      todos.forEach((todo) => {
        todo.tableIcon = MapStyles.getBmpTodoIconClasses(todo);
      });
    });
  };

  var addFcsIconClassesToTodos = function (todosBySubject) {
    _applyToAllSubjects(todosBySubject, (todos) => {
      todos.forEach((todo) => {
        todo.tableIcon = MapStyles.getFcsTodoIconClasses(todo);
      });
    });
  };

  var addScoreColorClasses = function (todosBySubject) {
    _applyToAllSubjects(todosBySubject, (todos) => {
      todos.forEach((todo) => {
        if (todo.score === undefined || todo.score === null) {
          todo.scoreBold = false;
        } else {
          todo.scoreColor = MapStyles.getConditionColor(+todo.score);
          todo.scoreBold = true;
        }
      });
    });
  };

  var roundScores = function (todosBySubject) {
    _applyToAllSubjects(todosBySubject, (todos) => {
      todos.forEach((todo) => {
        if (todo.score !== null && todo.score !== undefined) {
          todo.scoreRounded = (+todo.score).toLocaleString(undefined, {
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          });
        }
      });
    });
  };

  var addAssessmentDueDateColorClass = function (todos) {
    todos.forEach((todo) => {
      if (todo.days_remaining === undefined) return;
      if (todo.days_remaining === null || +todo.days_remaining < 1) {
        todo.assessmentDueDateColorClass = "red";
      } else {
        todo.assessmentDueDateColorClass = "";
      }
    });
  };

  var addAssessmentType = function (todos) {
    var dataView = Tree.get("dataView");
    if (dataView === "fcs") {
      addAssessmentTypeForFcsTodos(todos);
    } else if (dataView === "bmp") {
      addAssessmentTypeForBmpTodos(todos);
    }
  };

  var addAssessmentTypeForFcsTodos = function (todos) {
    todos.forEach((todo) => {
      if (todo.subject === "assessment due") {
        todo.assessmentType = "FCS Assessment";
        todo.assessmentTypeKey = "fcs";
      }
    });
  };

  var addAssessmentTypeForBmpTodos = function (todos) {
    todos.forEach((todo) => {
      if (todo.subject === "assessment due") {
        todo.skipBenchmark = BmpFunctions.getSkipBenchmark(todo);
        if ((todo.currentBenchmarkId && todo.currentBenchmarkComplete) || todo.skipBenchmark) {
          todo.assessmentType = "Assessment";
          todo.assessmentTypeKey = "bmp";
        } else {
          todo.assessmentType = "Benchmark";
          todo.assessmentTypeKey = "benchmark";
        }
      }
    });
  };

  var openFactSheet = function () {
    const { id } = ToDoList.getDataFromRow($(this));
    openFactSheetTo(id);
  };

  var openFactSheetTo = function (id, { highlightId = null } = {}) {
    ToDoConfig.getToDoDataViewConfig([dataView, "genericFactSheet"]).initFactSheet(id, highlightId);
  };

  var updateToDoData = async function (subject, todoIds, updateCallback) {
    const dataView = Tree.get("dataView");

    if (!DataViewFunctions.getCurrentDataViewProperty("smartToDoFilters", dataView)) {
      return;
    }

    const todoIdSet = new Set(todoIds);
    _updatePreparedData(subject, todoIdSet, updateCallback);
    await _updateOfflineData(dataView, subject, todoIdSet, updateCallback);
    loadTodos();
  };

  var _updatePreparedData = function (subject, todoIdSet, updateCallback) {
    if (!dataBySubject) return;

    const subjectData = dataBySubject[subject];

    for (let i = 0; i < subjectData.length; i++) {
      if (todoIdSet.has(subjectData[i].todoId)) {
        const todo = structuredClone(subjectData[i]);
        updateCallback(todo);
        subjectData[i] = todo;
      }
    }
  };

  var _updateOfflineData = async function (dataView, subject, todoIdSet, updateCallback) {
    const todos = dataBySubject[subject];

    const resourceKey = DataViewFunctions.getCurrentDataViewProperty("defaultLayers", dataView)[0];
    const updates = [];

    for (let i = 0; i < todos.length; i++) {
      if (todoIdSet.has(todos[i][subject]?.todoId)) {
        const todo = structuredClone(todos[i]);
        updateCallback(todo[subject]);
        updates.push(ResourceController.get(resourceKey).saveOffline(todo));
      }
    }
    await Promise.all(updates);
  };

  var unsetData = function () {
    dataBySubject = null;
  };

  var hasData = function () {
    return !!dataBySubject;
  };

  var setData = function (data) {
    if (!data) {
      unsetData();
      return;
    }

    _prepareAllData(data);
    rerenderToDoList();
    ToDoMapTable.update();
  };

  var getAllPreparedData = function () {
    if (!dataBySubject) {
      return {};
    }

    const preparedGroupedData = {};
    for (const subject in dataBySubject) {
      preparedGroupedData[subject] = _applyPrepareDataCallback(
        subject,
        Misc.shallowCloneArrayObjects(dataBySubject[subject]),
      );
    }
    return preparedGroupedData;
  };

  var _applyPrepareDataCallback = function (subject, todos) {
    const subjectConfig = ToDoConfig.getToDoSubjectConfig([subject]);
    if (subjectConfig?.prepareDataCallback) {
      return subjectConfig.prepareDataCallback(todos);
    }
    return todos;
  };

  var _applyToAllSubjects = function (todosBySubject, transformFunc) {
    for (const subject in todosBySubject) {
      transformFunc(todosBySubject[subject], subject);
    }
  };

  var getFilterSummary = function () {
    if (!filterSummary) {
      const filterFields = FilterFieldConstants.getToDoFilterFields();
      filterSummary = new FilterSummary(
        $(".to-do-filter-summary"),
        "smartToDoFilters",
        filterFields,
        ["todo"],
      );
    }

    return filterSummary;
  };

  return {
    init,
    loadTodos,
    rerenderToDoList,
    openIncompleteInventoryModalForBmp,
    openFactSheetTo,
    loadTodosIfNecessary,
    updateToDoData,
    hasData,
    unsetData,
    getAllPreparedData,
    getFilterSummary,
  };
};

module.exports = ToDoListController();

const Actions = require("../actions");
const BmpFcsInventoryModalController = require("../bmpfcs/bmpFcsInventoryModalController");
const BmpFcsToDoController = require("../bmpfcs/bmpFcsToDoController");
const BmpFunctions = require("../modals/bmpFunctions");
const DataViewFunctions = require("../dataViewFunctions");
const SortUtilities = require("../general/sortUtilities");
const MapStyles = require("./mapStyles");
const Misc = require("../misc");
const ResourceController = require("../offline/resourceController");
const TableDataFunctions = require("../general/tableDataFunctions");
const ToDoFunctions = require("./toDoFunctions");
const ToDoList = require("./toDoList");
const Tree = require("../tree");
const ToDoConfig = require("../config/toDoConfig");
const ToDoMapTable = require("./toDoMapTable");
const Filters = require("./filters");
const ResourceFilters = require("../filters/resourceFilters");
const FilterSummary = require("../filters/filterSummary");
const FilterFieldConstants = require("../config/filterFieldConstants");
